import React, { useState, useEffect } from 'react';
import './LegalLayout.css';

/**
 * LegalLayout - A reusable layout component for legal documents with sticky sidebar navigation
 * 
 * @param {string} title - The main title of the document
 * @param {string} lastUpdated - The last updated date (e.g., "January 1, 2024")
 * @param {Array<{id: string, title: string}>} sections - Array of section objects for navigation
 * @param {React.ReactNode} children - The content to display
 */
export default function LegalLayout({ title, lastUpdated, sections = [], children }) {
  const [activeSection, setActiveSection] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 150; // Offset for sticky header

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = document.getElementById(sections[i].id);
        if (section && section.offsetTop <= scrollPosition) {
          setActiveSection(sections[i].id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check

    return () => window.removeEventListener('scroll', handleScroll);
  }, [sections]);

  const handleNavClick = (e, sectionId) => {
    e.preventDefault();
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 100; // Account for sticky header
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });

      setShowMobileMenu(false);
    }
  };

  return (
    <div className="legal-layout">
      <div className="legal-container">
        {/* Mobile: Dropdown Navigation */}
        {isMobile && (
          <div className="legal-mobile-nav">
            <button
              className="legal-mobile-nav-button"
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              aria-expanded={showMobileMenu}
              aria-label="Jump to section"
            >
              Jump to section {showMobileMenu ? '▼' : '▶'}
            </button>
            {showMobileMenu && (
              <div className="legal-mobile-nav-menu">
                {sections.map((section) => (
                  <a
                    key={section.id}
                    href={`#${section.id}`}
                    onClick={(e) => handleNavClick(e, section.id)}
                    className={`legal-mobile-nav-link ${activeSection === section.id ? 'active' : ''}`}
                  >
                    {section.title}
                  </a>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Desktop: Sidebar Navigation */}
        {!isMobile && (
          <aside className="legal-sidebar">
            <nav className="legal-nav">
              <ul className="legal-nav-list">
                {sections.map((section) => (
                  <li key={section.id}>
                    <a
                      href={`#${section.id}`}
                      onClick={(e) => handleNavClick(e, section.id)}
                      className={`legal-nav-link ${activeSection === section.id ? 'active' : ''}`}
                    >
                      {section.title}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          </aside>
        )}

        {/* Main Content */}
        <main className="legal-content">
          <header className="legal-header">
            <h1 className="legal-title">{title}</h1>
            {lastUpdated && (
              <p className="legal-last-updated">Last updated: {lastUpdated}</p>
            )}
          </header>
          <div className="legal-body">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}

